import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FiArrowLeft, FiCalendar, FiClock } from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './BlogPage.css';

import studioImage from '../media/studio.png';
import civicsImage from '../media/blog_civics.png';
import tokenImage from '../media/blog_token.png';
import managerImage from '../media/blog_manager.png';
import hitlImage from '../media/blog_hitl.png';
import infoImage from '../media/blog_information.png';
import partyImage from '../media/blog_party.png';

type Post = {
  tag: string;
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
  slug: string;
  file: string;
  live: boolean;
};

const POST_IMAGES: Record<string, string> = {
  'why-we-built-codetunez': studioImage,
  'building-civics-tutor': civicsImage,
  'token-kit': tokenImage,
  'engineering-manager-agentic-era': managerImage,
  'hitl-hindrance': hitlImage,
  'hitl-bottleneck': infoImage,
  'where-code-meets-art': partyImage,
};

const BlogPostDetail: React.FC<{ post: Post }> = ({ post }) => {
  const [content, setContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;

    const loadMarkdown = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`/blog/${post.file}`);
        const markdown = await response.text();
        if (isMounted) {
          setContent(markdown);
        }
      } catch {
        if (isMounted) {
          setContent('## Unable to load this post\n\nPlease try again later.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadMarkdown();

    return () => {
      isMounted = false;
    };
  }, [post.file]);

  const postImage = POST_IMAGES[post.slug] || studioImage;

  return (
    <main className="blog-post">
      <div 
        className="blog-post__hero"
        style={{ backgroundImage: `url(${postImage})` }}
      >
        <div className="blog-post__hero-overlay" />
        <div className="container">
          <Link to="/blog" className="blog-page__back">
            <FiArrowLeft /> Back to Blog
          </Link>
          <div>
            <span className="blog-page__card-tag">{post.tag}</span>
          </div>
          <h1 className="blog-post__title">{post.title}</h1>
          <p className="blog-post__subtitle">{post.excerpt}</p>
          <div className="blog-post__meta">
            <span><FiCalendar size={13} /> {post.date}</span>
            <span><FiClock size={13} /> {post.readTime}</span>
          </div>
        </div>
      </div>

      <div className="container blog-post__body">
        {isLoading ? (
          <div className="blog-post__placeholder">
            <span>Loading post...</span>
          </div>
        ) : (
          <article className="blog-post__markdown">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
          </article>
        )}
      </div>
    </main>
  );
};

const BlogPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoadingPosts, setIsLoadingPosts] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;

    const loadPosts = async () => {
      setIsLoadingPosts(true);
      try {
        const response = await fetch('/blog/index.json');
        const postData = (await response.json()) as Post[];
        if (isMounted) {
          setPosts(postData);
        }
      } catch {
        if (isMounted) {
          setPosts([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingPosts(false);
        }
      }
    };

    loadPosts();

    return () => {
      isMounted = false;
    };
  }, []);

  const livePosts = useMemo(() => posts.filter((post) => post.live), [posts]);
  const selectedPost = useMemo(() => livePosts.find((post) => post.slug === slug), [livePosts, slug]);

  if (slug) {
    if (isLoadingPosts) {
      return (
        <main className="blog-page">
          <div className="container" style={{ paddingTop: 160, paddingBottom: 100 }}>
            <Link to="/blog" className="blog-page__back"><FiArrowLeft /> Back to Blog</Link>
            <h1>Loading post...</h1>
          </div>
        </main>
      );
    }

    if (!selectedPost) {
      return (
        <main className="blog-page">
          <div className="container" style={{ paddingTop: 160, paddingBottom: 100 }}>
            <Link to="/blog" className="blog-page__back"><FiArrowLeft /> Back to Blog</Link>
            <h1>Post not found</h1>
          </div>
        </main>
      );
    }

    return <BlogPostDetail post={selectedPost} />;
  }

  return (
    <main className="blog-page">
      <div className="blog-page__hero">
        <div className="container">
          <Link to="/" className="blog-page__back">
            <FiArrowLeft /> Back to Home
          </Link>
          <p className="section-label">Blog</p>
          <h1 className="section-title">
            Stories from the <span className="gradient-text">Studio</span>
          </h1>
          <p className="section-subtitle">
            Thoughts on software development, creative media production, and everything in between.
          </p>
        </div>
      </div>

      <div className="container blog-page__grid-wrap">
        {isLoadingPosts ? (
          <div className="blog-post__placeholder">
            <span>Loading posts...</span>
          </div>
        ) : (
          <div className="blog-page__grid">
            {livePosts.map((post, i) => (
              <Link to={`/blog/${post.slug}`} className="blog-page__card-link-wrap" key={i}>
                <article className="blog-page__card">
                  <div 
                    className="blog-page__card-img"
                    style={{ backgroundImage: `url(${POST_IMAGES[post.slug] || studioImage})` }}
                  />
                  <div className="blog-page__card-body">
                    <span className="blog-page__card-tag">{post.tag}</span>
                    <h2 className="blog-page__card-title">{post.title}</h2>
                    <p className="blog-page__card-excerpt">{post.excerpt}</p>
                    <div className="blog-page__meta">
                      <span><FiCalendar size={12} /> {post.date}</span>
                      <span><FiClock size={12} /> {post.readTime}</span>
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        )}
      </div>
    </main>
  );
};

export default BlogPage;
